import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import DataTable from "@/components/DataTable";
import { X, UsersThree, Phone, PencilSimple, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import "./dashboard-premium.css";

/**
 * Team page — mirror view of the staff assigned to Communication Seats.
 *
 * Per Feb 2026 owner spec:
 *   • Staff can ONLY be added via the Numbers page (Assign a seat there
 *     creates a new user). No "Add member" button here.
 *   • Team size cannot exceed the seat count. Backend enforces this too.
 *   • Editing a row updates the assigned user's username / password /
 *     display name in place — same primitive the Numbers-page reassign
 *     dialog uses (PATCH /api/numbers/{num_id}/assignee).
 *   • Removing a row UNASSIGNS the user from their seat (seat becomes
 *     vacant on the Numbers page and can be re-assigned later). The user
 *     account is deactivated so they lose access immediately.
 */
export default function Team() {
  const [members, setMembers] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [editing, setEditing] = useState(null); // { member, number }
  const [err, setErr] = useState("");
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      // /numbers and /numbers/entitlement remained admin-only in earlier
      // builds. Supervisor now has read access (iter124), but degrade
      // gracefully if they don't so the Team directory stays usable.
      const [m, n, e] = await Promise.all([
        api.get("/team"),
        api.get("/numbers").catch(() => ({ data: [] })),
        api.get("/numbers/entitlement").catch(() => ({ data: null })),
      ]);
      setMembers(m.data || []);
      setNumbers(n.data || []);
      if (e.data) setEntitlement(e.data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Map user_id → the Communication Seat they own.
  const seatByUser = numbers.reduce((acc, n) => {
    if (n.assign_target_type === "user" && n.assign_target_id) {
      acc[n.assign_target_id] = n;
    }
    return acc;
  }, {});

  // Only agents/supervisors/qa counted as "staff seats" — admin themselves
  // does not consume a staff seat unless they've bought their own personal
  // Communication Seat (which shows up as any other assigned seat).
  const staffMembers = members.filter((m) => m.role !== "admin" && m.role !== "owner");
  const totalSeats = entitlement?.total_seats ?? entitlement?.seats_total ?? entitlement?.limit ?? numbers.length;
  const seatsUsed = staffMembers.length;
  const canAddMore = seatsUsed < totalSeats;

  return (
    <div className="hbf-premium-scope hv-exec p-8" data-testid="team-page">
      <header className="hd-fade-up flex items-end justify-between gap-6 flex-wrap" style={{ marginBottom: 30 }}>
        <div>
          <div className="hv-eyebrow">Organization · Team directory</div>
          <h1 className="hv-title" style={{ marginTop: 8 }}>Your <strong>team</strong></h1>
          <p className="hv-sub" style={{ marginTop: 8 }}>
            {seatsUsed} of {totalSeats} Communication Seat{totalSeats === 1 ? "" : "s"} assigned
            {members.length !== staffMembers.length && (
              <span className="text-[var(--text-muted)]"> · plus {members.length - staffMembers.length} admin</span>
            )}
          </p>
        </div>
        {user?.role === "admin" && (
          <Link
            to={canAddMore ? "/app/numbers" : "/app/settings#manage-seats"}
            className="hd-btn hd-btn--primary"
            data-testid="team-manage-seats-btn"
            title={canAddMore
              ? "Assign an unassigned Communication Seat to a new staff member"
              : "All seats are assigned. Add more seats to your plan first."}
          >
            <UsersThree size={14} weight="fill" />
            {canAddMore ? "Assign a seat to add member" : "Add more seats to plan"}
          </Link>
        )}
      </header>

      {/* Info banner explaining the seat-based membership rule */}
      {user?.role === "admin" && (
        <div
          className="hd-fade-up flex items-start gap-3"
          style={{
            padding: "12px 16px", borderRadius: 12, marginBottom: 18,
            border: "1px solid var(--hd-border,rgba(148,163,184,.2))",
            background: "var(--hd-bg-subtle,rgba(37,99,235,.04))",
          }}
          data-testid="team-seat-rule-banner"
        >
          <Phone size={16} weight="fill" className="text-[var(--accent)] mt-0.5 shrink-0" />
          <div className="flex-1 text-[13px] text-[var(--text-secondary)] leading-relaxed">
            <strong>Team size = seat count.</strong> To add a new staff member, go to
            <Link to="/app/numbers" className="mx-1 font-semibold text-[var(--accent)]">Numbers</Link>
            and assign a seat. To replace someone, edit their row here — the number stays put, only the person behind it changes.
          </div>
        </div>
      )}

      {err && <div className="hd-notice hd-notice--danger" data-testid="team-err"><X size={16} weight="bold" /><div>{err}</div></div>}

      <div className="mt-2 hd-fade-up hd-stagger-1">
        {members.length === 0 ? (
          <div className="hd-card hd-card--static" style={{ padding: 8 }}>
            <EmptyState
              icon="team"
              title="Build your team"
              description={user?.role === "admin"
                ? "Staff are added by assigning them a Communication Seat on the Numbers page. Every seat = one active staff member."
                : "Your admin hasn't assigned any Communication Seats yet. Once seats are provisioned, your teammates will appear here."}
              primaryAction={user?.role === "admin" ? { label: "Go to Numbers", onClick: () => (window.location.href = "/app/numbers") } : undefined}
              learnMore={user?.role === "admin" ? { label: "Roles & permissions", href: "/legal/aup" } : undefined}
              testId="empty-state-team"
            />
          </div>
        ) : (
          <div className="hd-card hd-card--static" style={{ padding: 6 }}>
            <DataTable
              testId="team-table"
              data={members}
              rowKey={(m) => m.id}
              rowTestId={(m) => `team-row-${m.id}`}
              searchPlaceholder="Search team by name, email or username…"
              searchKeys={["name", "email", "username", "role"]}
              filters={[
                { key: "role", label: "Role", options: [
                  { value: "admin", label: "Admin" },
                  { value: "supervisor", label: "Supervisor" },
                  { value: "agent", label: "Agent" },
                ]},
              ]}
              columns={[
                {
                  key: "name", label: "Member", sortable: true,
                  render: (m) => (
                    <div className="flex items-center gap-3">
                      <img src={m.avatar_url} className="w-8 h-8 border border-[var(--border)]" alt="" />
                      <div>
                        <div className="font-semibold">{m.name || m.username}</div>
                        <div className="text-[11px] text-[var(--text-muted)] font-mono">@{m.username}</div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "_seat", label: "Assigned Number", width: "180px",
                  render: (m) => {
                    const seat = seatByUser[m.id];
                    if (!seat) {
                      return <span className="text-[12px] text-[var(--text-muted)]">— no seat —</span>;
                    }
                    return (
                      <div className="flex items-center gap-2 font-mono text-[13px]" data-testid={`team-row-phone-${m.id}`}>
                        <Phone size={12} weight="fill" className="text-[var(--accent)]" /> {seat.phone || seat.phone_e164}
                      </div>
                    );
                  },
                },
                {
                  key: "role", label: "Role", sortable: true, width: "120px",
                  render: (m) => {
                    const cls =
                      m.role === "admin"      ? "pill-outbound" :
                      m.role === "supervisor" ? "pill-inprogress" :
                      m.role === "quality"    ? "pill-completed" :
                      "pill-inbound";
                    return <span className={`pill ${cls}`}>{m.role}</span>;
                  },
                },
                { key: "calls_today", label: "Calls today", sortable: true, width: "110px", align: "right", render: (m) => <span className="font-mono">{m.calls_today}</span> },
                { key: "created_at", label: "Joined", sortable: true, width: "120px", render: (m) => <span className="text-[12px] text-[var(--text-secondary)]">{new Date(m.created_at).toLocaleDateString()}</span> },
                ...(user?.role === "admin" ? [{
                  key: "_act", label: "", width: "110px", align: "right",
                  render: (m) => {
                    if (m.id === user.id || m.role === "admin" || m.role === "owner") return null;
                    const seat = seatByUser[m.id];
                    return (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing({ member: m, number: seat || null })}
                          title="Edit username / password / display name"
                          data-testid={`team-edit-${m.id}`}
                          className="p-1.5 text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
                        >
                          <PencilSimple size={14} weight="fill" />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(
                              `Remove ${m.name || m.username} from the team?\n\n` +
                              (seat
                                ? `Their Communication Seat (${seat.phone || seat.phone_e164}) stays in your organization and becomes available to reassign to a new staff member on the Numbers page. ${m.name || m.username} will lose access immediately.`
                                : `${m.name || m.username} will lose access immediately.`),
                            )) return;
                            try {
                              await api.delete(`/team/${m.id}`);
                              load();
                            } catch (ex) {
                              alert(formatApiError(ex.response?.data?.detail) || ex.message);
                            }
                          }}
                          title={seat ? "Unassign from seat" : "Remove from team"}
                          data-testid={`team-remove-${m.id}`}
                          className="p-1.5 text-[var(--text-muted)] hover:text-[var(--danger)] hover:bg-red-50 transition-colors"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    );
                  },
                }] : []),
              ]}
            />
          </div>
        )}
      </div>

      {editing && (
        <EditTeamMemberDialog
          member={editing.member}
          number={editing.number}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline edit dialog — reuses PATCH /api/numbers/{id}/assignee so the same
// primitive drives both Numbers-page reassign and Team-page edit.
// ---------------------------------------------------------------------------
function EditTeamMemberDialog({ member, number, onClose, onSaved }) {
  const [form, setForm] = useState({
    username: member.username || "",
    password: "",
    display_name: member.name || "",
    role: member.role || "agent",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = (
    (form.username.trim().length >= 2 && form.username !== member.username) ||
    (form.password.length >= 6) ||
    (form.display_name.trim() !== (member.name || "").trim()) ||
    (form.role !== (member.role || "agent"))
  );

  async function submit() {
    setBusy(true); setErr("");
    try {
      const payload = {};
      if (form.username.trim() !== member.username) payload.username = form.username.trim();
      if (form.password) payload.password = form.password;
      if (form.display_name.trim() !== (member.name || "").trim()) payload.display_name = form.display_name.trim();
      if (form.role !== (member.role || "agent")) payload.role = form.role;
      await api.patch(`/team/${member.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="hd-modal-scrim" onClick={onClose} data-testid="team-edit-scrim">
      <div className="hd-modal w-full max-w-lg" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid="team-edit-dialog">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-[18px]">Edit team member</div>
            <div className="text-[12px] text-[var(--text-muted)] font-mono mt-0.5">
              {number ? (number.phone || number.phone_e164) : "No Communication Seat"}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          {err && <div className="text-[13px] text-[var(--danger)]" data-testid="team-edit-err">{err}</div>}
          <div>
            <label className="label">Display name</label>
            <input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="input w-full"
              data-testid="team-edit-display_name"
            />
          </div>
          <div>
            <label className="label">Username</label>
            <input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="input w-full"
              data-testid="team-edit-username"
            />
          </div>
          <div>
            <label className="label">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="input w-full"
              data-testid="team-edit-role"
            >
              <option value="agent">Agent — dialer, messages, contacts</option>
              <option value="supervisor">Supervisor — monitor, whisper, barge, leaderboard</option>
              <option value="quality">Quality — recordings review only</option>
            </select>
            <div className="text-[11px] text-[var(--text-muted)] mt-1">
              Changing the role updates their access immediately on next page load.
            </div>
          </div>
          <div>
            <label className="label">New password <span className="text-[11px] text-[var(--text-muted)]">(min 6, leave blank to keep current)</span></label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input w-full"
              placeholder="••••••"
              data-testid="team-edit-password"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <div className="text-[11px] text-[var(--text-muted)]">
            {number
              ? "Number is permanent — only the person behind it changes."
              : "This member is not bound to a Communication Seat."}
          </div>
          <div className="flex gap-2">
            <button className="hd-btn hd-btn--ghost" onClick={onClose} disabled={busy} data-testid="team-edit-cancel">Cancel</button>
            <button
              className="hd-btn hd-btn--primary"
              onClick={submit}
              disabled={busy || !canSubmit}
              data-testid="team-edit-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
